import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchStatsSummary } from "../services/statsService";
import { StatsCounterItem, StatsSummaryItem } from "../types/stats";
import { PilotStatsData, UsePilotStatsResult } from "./usePilotStats";

const LIMIT = 100;

export interface TypicalPilot {
  kills: number;
  durationSeconds: number;
}

export interface UseCommunityStatsResult extends UsePilotStatsResult {
  typical: TypicalPilot | null;
}

// Summary rows carry community totals; shape them like a pilot's own counters so one view renders both.
const asCounter = (row: StatsSummaryItem): StatsCounterItem => ({
  metric_name: row.metric_name,
  schema_name: row.schema_name,
  pilot_uid: null,
  tags: row.tags,
  event_count: row.event_count,
  value_total: row.value_total,
  value_average: null,
  first_occurred_at: null,
  last_occurred_at: null,
});

export default function useCommunityStats(): UseCommunityStatsResult {
  const [data, setData] = useState<PilotStatsData | null>(null);
  const [typical, setTypical] = useState<TypicalPilot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const [totalKills, killsByAirframe, killsByWeaponFamily, killsByVictim, totalDuration, durationByAirframe, totalLandings, carrierLandings, landingsByType] =
        await Promise.all([
          fetchStatsSummary({ schema_name: "TOTAL_KILLS", limit: 1 }),
          fetchStatsSummary({ schema_name: "KILLS_BY_AIRFRAME", order: "event_count", direction: "desc", limit: LIMIT }),
          fetchStatsSummary({ schema_name: "KILLS_BY_WEAPON_FAMILY", order: "event_count", direction: "desc", limit: LIMIT }),
          fetchStatsSummary({ schema_name: "KILLS_BY_VICTIM_DOMAIN_AND_CATEGORY", order: "event_count", direction: "desc", limit: LIMIT }),
          fetchStatsSummary({ schema_name: "TOTAL_DURATION", limit: 1 }),
          fetchStatsSummary({ schema_name: "DURATION_BY_AIRFRAME", order: "value_total", direction: "desc", limit: LIMIT }),
          fetchStatsSummary({ schema_name: "LANDINGS", limit: 1 }),
          fetchStatsSummary({ schema_name: "CARRIER_LANDINGS", limit: 1 }),
          fetchStatsSummary({ schema_name: "LANDINGS_BY_TYPE", order: "event_count", direction: "desc", limit: LIMIT }),
        ]);
      if (id !== requestId.current) return;

      setData({
        totalKills: totalKills.items[0] ? asCounter(totalKills.items[0]) : null,
        killsByAirframe: killsByAirframe.items.map(asCounter),
        killsByWeaponFamily: killsByWeaponFamily.items.map(asCounter),
        killsByVictimDomainAndCategory: killsByVictim.items.map(asCounter),
        totalDuration: totalDuration.items[0] ? asCounter(totalDuration.items[0]) : null,
        durationByAirframe: durationByAirframe.items.map(asCounter),
        totalLandings: totalLandings.items[0] ? asCounter(totalLandings.items[0]) : null,
        carrierLandings: carrierLandings.items[0] ? asCounter(carrierLandings.items[0]) : null,
        landingsByType: landingsByType.items.map(asCounter),
        gauges: [],
      });
      setTypical({
        kills: totalKills.items[0]?.median_event_count ?? 0,
        durationSeconds: totalDuration.items[0]?.median_value_total ?? 0,
      });
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err instanceof Error ? err.message : "Unexpected error");
      setData(null);
      setTypical(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(() => { void load(); }, [load]);
  return useMemo(() => ({ data, typical, loading, error, refresh }), [data, typical, loading, error, refresh]);
}
